/**
 * R1ReviewPage — Admin chấm + review điểm Vòng Sơ loại flow `dual_claimant_packets`.
 *
 * H.4 spec: bảng đội với 4 cột /50 + tổng /200 + video /30 + AI/admin/lock status.
 * 6 actions: Run AI / Re-run failed / Save review / Lock preliminary / Generate shortlist / Publish top 4.
 *
 * KHÔNG có judge view ở đây — R1 chỉ AI + Admin (H.5).
 */
import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  Assessment,
  AutoAwesome,
  Lock,
  Replay,
  Save,
  EmojiEvents,
  Publish,
  Edit,
} from '@mui/icons-material'

import { useAppSelector } from '../hooks/redux'
import { useHasRole } from '../hooks/usePermission'
import { useGetCasesQuery } from '../store/api/casesApi'
import {
  useGetR1RankingQuery,
  useListR1ScorecardsQuery,
  useRunR1AiScoringMutation,
  useUpdateR1ScorecardMutation,
  useLockR1ScorecardMutation,
  useLockR1TeamMutation,
  useGenerateR1ShortlistMutation,
  usePublishR1Top4Mutation,
  type R1Side,
  type R1Part,
  type R1TeamScoreSummary,
  type R1Scorecard,
} from '../store/api/r1ScoringApi'
import { hasPermission } from '../utils/permissions'
import { PageHeader } from '../components/PageHeader'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'

const SIDE_PART_KEYS: Array<{ side: R1Side; part: R1Part; label: string; bg: string; fg: string }> = [
  { side: 'scenario_a', part: 'memorial', label: 'Hồ sơ 1 / Đơn', bg: '#fce4ec', fg: '#c2185b' },
  { side: 'scenario_a', part: 'evidence', label: 'Hồ sơ 1 / Chứng cứ', bg: '#fce4ec', fg: '#c2185b' },
  { side: 'scenario_b', part: 'memorial', label: 'Hồ sơ 2 / Đơn', bg: '#e3f2fd', fg: '#1565c0' },
  { side: 'scenario_b', part: 'evidence', label: 'Hồ sơ 2 / Chứng cứ', bg: '#e3f2fd', fg: '#1565c0' },
]

function statusChip(status: 'pending' | 'partial' | 'done' | 'failed') {
  if (status === 'done') return <Chip size="small" label="Hoàn tất" color="success" />
  if (status === 'partial') return <Chip size="small" label="Một phần" color="info" />
  if (status === 'failed') return <Chip size="small" label="Lỗi" color="error" />
  return <Chip size="small" label="Chưa chạy" variant="outlined" />
}

function reviewChip(status: 'pending' | 'partial' | 'done') {
  if (status === 'done') return <Chip size="small" label="Đã review" color="success" />
  if (status === 'partial') return <Chip size="small" label="Một phần" color="warning" />
  return <Chip size="small" label="Chưa review" variant="outlined" />
}

export default function R1ReviewPage() {
  const { user: currentUser } = useAppSelector((s) => s.auth)
  const isAdmin = useHasRole(['secretary'])
  const canAccess = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')

  const { data: cases = [] } = useGetCasesQuery()
  const dualCases = useMemo(
    () => cases.filter((c) => c.r1_flow_version === 'dual_claimant_packets'),
    [cases],
  )

  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  useMemo(() => {
    if (!selectedCaseId && dualCases.length > 0) setSelectedCaseId(dualCases[0].id)
  }, [selectedCaseId, dualCases])

  const selectedCase = dualCases.find((c) => c.id === selectedCaseId)

  const { data: ranking = [], isFetching: isFetchingRanking, refetch: refetchRanking } =
    useGetR1RankingQuery({ case_id: selectedCaseId }, { skip: !selectedCaseId })

  const { data: allScorecards = [], refetch: refetchScorecards } = useListR1ScorecardsQuery(
    { case_id: selectedCaseId },
    { skip: !selectedCaseId },
  )

  const [runAi, { isLoading: isRunningAi }] = useRunR1AiScoringMutation()
  const [updateScorecard, { isLoading: isUpdating }] = useUpdateR1ScorecardMutation()
  const [lockScorecard] = useLockR1ScorecardMutation()
  const [lockTeam] = useLockR1TeamMutation()
  const [generateShortlist, { isLoading: isGeneratingShortlist }] = useGenerateR1ShortlistMutation()
  const [publishTop4, { isLoading: isPublishing }] = usePublishR1Top4Mutation()

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  })
  const showMsg = (message: string, severity: 'success' | 'error' | 'info' = 'info') =>
    setSnackbar({ open: true, message, severity })

  // Edit dialog
  const [editTarget, setEditTarget] = useState<{ team: R1TeamScoreSummary; side: R1Side; part: R1Part } | null>(null)
  const [editScore, setEditScore] = useState<number>(0)

  const openEdit = (team: R1TeamScoreSummary, side: R1Side, part: R1Part) => {
    const key = `${side}_${part}`
    const currentScore =
      side === 'scenario_a'
        ? part === 'memorial'
          ? team.scenario_a_memorial
          : team.scenario_a_evidence
        : part === 'memorial'
        ? team.scenario_b_memorial
        : team.scenario_b_evidence
    setEditScore(currentScore)
    setEditTarget({ team, side, part })
  }

  const closeEdit = () => {
    setEditTarget(null)
    setEditScore(0)
  }

  // Tìm scorecard hiện hành (effective) của 1 (team, side, part)
  const findEffectiveScorecard = (team_id: string, side: R1Side, part: R1Part): R1Scorecard | undefined =>
    allScorecards.find(
      (sc) => sc.team_id === team_id && sc.r1_side === side && sc.part === part && sc.is_effective,
    )

  const handleSaveEdit = async () => {
    if (!editTarget || !selectedCaseId) return
    if (editScore < 0 || editScore > 50) {
      showMsg('Điểm phải nằm trong [0, 50].', 'error')
      return
    }
    const effective = findEffectiveScorecard(editTarget.team.team_id, editTarget.side, editTarget.part)
    try {
      if (effective) {
        await updateScorecard({
          scorecard_id: effective.id,
          body: { total_score: editScore, status: 'submitted' },
        }).unwrap()
      } else {
        // Chưa có scorecard nào → tạo bằng cách chạy AI fail (tạo placeholder) rồi update.
        // Đơn giản hơn: gọi PATCH với 1 dummy id sẽ 404 → admin cần Run AI trước.
        showMsg('Đội này chưa có scorecard. Vui lòng Run AI trước hoặc liên hệ dev.', 'error')
        return
      }
      showMsg('Đã lưu điểm.', 'success')
      closeEdit()
      refetchRanking()
      refetchScorecards()
    } catch (e: any) {
      showMsg(e?.data?.detail || 'Lưu thất bại', 'error')
    }
  }

  const handleRunAi = async (only_failed = false) => {
    if (!selectedCaseId) return
    try {
      const res = await runAi({ case_id: selectedCaseId, only_failed }).unwrap()
      const okCount = Object.values(res.results).reduce(
        (sum, perTeam) => sum + Object.values(perTeam).filter((v) => v === 'ok').length,
        0,
      )
      const failCount = Object.values(res.results).reduce(
        (sum, perTeam) => sum + Object.values(perTeam).filter((v) => v.startsWith('failed:')).length,
        0,
      )
      showMsg(
        `Đã chạy AI: ${okCount} thành công, ${failCount} thất bại. Mở từng đội để xem chi tiết.`,
        failCount > 0 ? 'info' : 'success',
      )
      refetchRanking()
      refetchScorecards()
    } catch (e: any) {
      showMsg(e?.data?.detail || 'Chạy AI thất bại', 'error')
    }
  }

  const handleLockTeam = async (team_id: string) => {
    if (!selectedCaseId) return
    try {
      const res = await lockTeam({ case_id: selectedCaseId, team_id }).unwrap()
      showMsg(`Đã khóa ${res.locked_count} phiếu chấm.`, 'success')
      refetchRanking()
      refetchScorecards()
    } catch (e: any) {
      showMsg(e?.data?.detail || 'Khóa thất bại', 'error')
    }
  }

  // Shortlist dialog
  const [shortlistDialog, setShortlistDialog] = useState<{ open: boolean; topN: number; preview: R1TeamScoreSummary[] }>({
    open: false,
    topN: 15,
    preview: [],
  })
  const handleOpenShortlist = () => {
    const n = selectedCase?.r1_shortlist_size || 15
    setShortlistDialog({ open: true, topN: n, preview: [] })
  }
  const handleGenerateShortlist = async () => {
    if (!selectedCaseId) return
    try {
      const res = await generateShortlist({ case_id: selectedCaseId, top_n: shortlistDialog.topN }).unwrap()
      setShortlistDialog((s) => ({ ...s, preview: res }))
    } catch (e: any) {
      showMsg(e?.data?.detail || 'Tạo shortlist thất bại', 'error')
    }
  }

  // Publish Top 4 dialog
  const [publishDialog, setPublishDialog] = useState<{ open: boolean; topN: number }>({
    open: false,
    topN: 4,
  })

  const handleConfirmPublish = async () => {
    if (!selectedCaseId) return
    try {
      const res = await publishTop4({ case_id: selectedCaseId, top_n: publishDialog.topN }).unwrap()
      showMsg(
        `Đã công bố ${res.advanced_team_ids.length} đội vào Vòng Bán kết.`,
        'success',
      )
      setPublishDialog({ open: false, topN: publishDialog.topN })
      refetchRanking()
    } catch (e: any) {
      showMsg(e?.data?.detail || 'Publish thất bại', 'error')
    }
  }

  if (!canAccess) {
    return (
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Alert severity="error">Bạn không có quyền truy cập trang Chấm điểm R1.</Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Chấm điểm Vòng Sơ loại"
        subtitle="Flow dual_claimant_packets — Admin chấm 4 phần (2 hồ sơ × Đơn + Chứng cứ). Tổng tối đa 200đ. AI hỗ trợ ban đầu, Admin chốt cuối."
        icon={<Assessment />}
      />

      {/* Filter bar */}
      <Paper elevation={0} sx={(t) => ({ ...getFilterPaperSx(t), mb: 2 })}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { md: 360 } }}>
            <InputLabel>Cuộc thi</InputLabel>
            <Select
              value={selectedCaseId}
              label="Cuộc thi"
              onChange={(e) => setSelectedCaseId(e.target.value)}
            >
              {dualCases.length === 0 ? (
                <MenuItem value="" disabled>
                  Không có cuộc thi dùng flow mới
                </MenuItem>
              ) : (
                dualCases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title || '— Chưa đặt tên —'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: { md: 'auto' } }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AutoAwesome />}
              disabled={!selectedCaseId || isRunningAi}
              onClick={() => handleRunAi(false)}
            >
              {isRunningAi ? 'Đang chạy AI...' : 'Run AI scoring'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Replay />}
              disabled={!selectedCaseId || isRunningAi}
              onClick={() => handleRunAi(true)}
            >
              Re-run failed only
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<EmojiEvents />}
              disabled={!selectedCaseId || isGeneratingShortlist}
              onClick={handleOpenShortlist}
            >
              Generate shortlist
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Publish />}
              disabled={!selectedCaseId || isPublishing}
              onClick={() => setPublishDialog({ open: true, topN: 4 })}
            >
              Publish top 4 to Semifinal
            </Button>
          </Box>
        </Stack>
      </Paper>

      {!selectedCaseId ? (
        <Alert severity="info">Chọn cuộc thi để bắt đầu chấm điểm.</Alert>
      ) : isFetchingRanking && ranking.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : ranking.length === 0 ? (
        <Alert severity="info">Chưa có đội nào được phân bổ vào cuộc thi này.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(t) => getTablePaperSx(t)}>
          <Table size="small" sx={{ minWidth: 1280 }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'background.default', whiteSpace: 'nowrap' } }}>
                <TableCell><strong>Đội</strong></TableCell>
                {SIDE_PART_KEYS.map((sp) => (
                  <TableCell key={`${sp.side}_${sp.part}`}>
                    <Chip
                      size="small"
                      label={sp.label}
                      sx={{ bgcolor: sp.bg, color: sp.fg, fontWeight: 600 }}
                    />
                  </TableCell>
                ))}
                <TableCell align="right"><strong>Tổng /200</strong></TableCell>
                <TableCell align="right"><strong>Video /30</strong></TableCell>
                <TableCell><strong>AI</strong></TableCell>
                <TableCell><strong>Admin review</strong></TableCell>
                <TableCell><strong>Lock</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ranking.map((team) => (
                <TableRow key={team.team_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {team.team_name || '— Đội chưa rõ —'}
                    </Typography>
                  </TableCell>
                  {SIDE_PART_KEYS.map((sp) => {
                    const key = `${sp.side}_${sp.part}`
                    const score =
                      sp.side === 'scenario_a'
                        ? sp.part === 'memorial'
                          ? team.scenario_a_memorial
                          : team.scenario_a_evidence
                        : sp.part === 'memorial'
                        ? team.scenario_b_memorial
                        : team.scenario_b_evidence
                    const status = team.statuses[key] || 'missing'
                    const isLocked = status === 'locked'
                    return (
                      <TableCell key={key}>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={status === 'missing' ? 'text.disabled' : 'text.primary'}
                          >
                            {status === 'missing' ? '—' : score.toFixed(1)}
                          </Typography>
                          {status === 'ai' && <Chip size="small" label="AI" variant="outlined" />}
                          {status === 'admin' && (
                            <Chip size="small" label="Admin" color="primary" variant="outlined" />
                          )}
                          {!isLocked && (
                            <Tooltip title="Sửa điểm">
                              <span>
                                <Button
                                  size="small"
                                  onClick={() => openEdit(team, sp.side, sp.part)}
                                  sx={{ minWidth: 0, p: 0.5 }}
                                  disabled={status === 'missing'}
                                >
                                  <Edit fontSize="small" />
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    )
                  })}
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                      {team.r1_total.toFixed(1)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      label={
                        team.video_tiebreak_total != null
                          ? `${team.video_tiebreak_total.toFixed(1)} / 30`
                          : '—'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{statusChip(team.ai_status)}</TableCell>
                  <TableCell>{reviewChip(team.admin_review_status)}</TableCell>
                  <TableCell>
                    {team.locked ? (
                      <Chip size="small" icon={<Lock fontSize="small" />} label="Locked" color="success" />
                    ) : (
                      <Chip size="small" label="Mở" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Khóa 4 phiếu hiện hành của đội">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<Lock fontSize="small" />}
                            disabled={team.locked || team.parts_scored === 0}
                            onClick={() => handleLockTeam(team.team_id)}
                          >
                            Lock
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={closeEdit} maxWidth="xs" fullWidth>
        <DialogTitle>Sửa điểm</DialogTitle>
        <DialogContent>
          {editTarget && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>{editTarget.team.team_name}</strong> — {' '}
                {SIDE_PART_KEYS.find((s) => s.side === editTarget.side && s.part === editTarget.part)?.label}
              </Typography>
              <TextField
                label="Điểm (0–50)"
                type="number"
                value={editScore}
                onChange={(e) =>
                  setEditScore(Math.max(0, Math.min(50, Number(e.target.value) || 0)))
                }
                inputProps={{ min: 0, max: 50, step: 0.5 }}
                fullWidth
              />
              <Alert severity="info" variant="outlined">
                Sửa điểm AI sẽ TẠO scorecard Admin mới (supersedes) — không mất history.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>Hủy</Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveEdit}
            disabled={isUpdating}
          >
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shortlist dialog */}
      <Dialog
        open={shortlistDialog.open}
        onClose={() => setShortlistDialog({ ...shortlistDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generate shortlist</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Top N"
              type="number"
              value={shortlistDialog.topN}
              onChange={(e) =>
                setShortlistDialog({
                  ...shortlistDialog,
                  topN: Math.max(1, Number(e.target.value) || 1),
                })
              }
              inputProps={{ min: 1, step: 1 }}
              fullWidth
            />
            <Button variant="contained" onClick={handleGenerateShortlist} disabled={isGeneratingShortlist}>
              Tạo preview
            </Button>
            {shortlistDialog.preview.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Đội</TableCell>
                      <TableCell align="right">/200</TableCell>
                      <TableCell align="right">Video /30</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shortlistDialog.preview.map((t, idx) => (
                      <TableRow key={t.team_id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{t.team_name}</TableCell>
                        <TableCell align="right">{t.r1_total.toFixed(1)}</TableCell>
                        <TableCell align="right">
                          {t.video_tiebreak_total != null ? t.video_tiebreak_total.toFixed(1) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortlistDialog({ ...shortlistDialog, open: false })}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Publish Top 4 dialog */}
      <Dialog
        open={publishDialog.open}
        onClose={() => setPublishDialog({ ...publishDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish top 4 to Semifinal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning">
              Hành động này set <code>competition_status='advanced_to_semifinal'</code> cho top N
              đội theo ranking hiện tại. Các đội này sẽ vào Vòng Bán kết.
            </Alert>
            <TextField
              label="Top N (mặc định 4)"
              type="number"
              value={publishDialog.topN}
              onChange={(e) =>
                setPublishDialog({
                  ...publishDialog,
                  topN: Math.max(1, Number(e.target.value) || 4),
                })
              }
              inputProps={{ min: 1, step: 1 }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialog({ ...publishDialog, open: false })}>Hủy</Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Publish />}
            onClick={handleConfirmPublish}
            disabled={isPublishing}
          >
            Xác nhận publish
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
